/**
 * Content Monitoring — слой данных (реальный CM API).
 *
 * CM — отдельный сервис (FastAPI + ClickHouse) со своим доменом; CORS у него
 * открыт, поэтому ходим напрямую. Базовый URL — VUE_APP_CM_API (см. vue.config.js).
 *
 * Здесь же живёт вся развязка форматов: ответы CM приводятся к тем формам,
 * которые ждут компоненты. Что реально отличается от прежних моков:
 *   · summary НЕ отдаёт дельты — проценты роста у KPI убраны;
 *   · timelines НЕ принимает granularity: API всегда даёт дневной ряд,
 *     неделя/месяц агрегируются на клиенте (aggregateSeries);
 *   · поля другие: rel_activity/rel_discussion, trends/new_signals,
 *     author_weight = { by_activity, by_reach }, лента на limit/offset.
 *
 * Связка страниц: в CM page_id — это id VK-сообщества, а в Lucmus то же самое
 * лежит в owner_id. Поэтому VK-страницы связываются напрямую, а страницы других
 * соцсетей в CM просто отсутствуют (модуль пока только про VK).
 */

import axios from "axios";

const http = axios.create({
  baseURL: process.env.VUE_APP_CM_API,
  timeout: 30000,
});

/**
 * CM проверяет тот же JWT, что и бэкенд Lucmus, и сам выводит из него workspace
 * (id пользователя). Токен читаем на каждом запросе: при логине/логауте он
 * меняется, а инстанс axios живёт всё время.
 */
http.interceptors.request.use((request) => {
  const token = localStorage.getItem("user-token");
  if (token) request.headers.authorization = `bearer ${token}`;
  return request;
});

const PREFIX = "/content-monitoring";

/**
 * Часовой пояс пользователя. В профиле Lucmus его нет (там только email/locale),
 * поэтому берём из браузера — как и остальные графики приложения, которые рисуют
 * часы в локальном времени (см. models/page-stat.js). Нужен только «ритму слова»
 * в drill-down; за сессию не меняется — считаем один раз.
 */
const BROWSER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return "UTC";
  }
})();

/**
 * Ошибку CM показываем как есть — она информативнее «Network Error».
 *
 * detail у FastAPI бывает не только строкой: на 422 это массив объектов
 * ({loc, msg, type}). Без разбора такой detail превращался в «[object Object]».
 */
function toMessage(error) {
  const detail = error.response && error.response.data && error.response.data.detail;

  if (typeof detail === "string" && detail) return detail;

  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => (d && (d.msg || d.message)) || JSON.stringify(d)).join("; ");
  }

  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  return error.message || "Content Monitoring недоступен";
}

const get = (path, params) =>
  http.get(PREFIX + path, { params }).then((r) => r.data).catch((e) => {
    throw new Error(toMessage(e));
  });

const post = (path, body, config) =>
  http.post(PREFIX + path, body, config).then((r) => r.data).catch((e) => {
    throw new Error(toMessage(e));
  });

/* ---------- период и страницы ---------- */

function period(params) {
  const p = params.period || {};
  return {
    date_from: params.from || p.start || undefined,
    date_to: params.to || p.end || undefined,
  };
}

/**
 * Кэш списка страниц CM: нужен, чтобы понять, какие из страниц Lucmus вообще
 * заведены в мониторинге. Список меняется редко — держим одно обещание.
 *
 * Кэш с TTL, а не навсегда: страницу могли завести в мониторинге уже после
 * загрузки приложения, и без протухания она не появилась бы до перезагрузки.
 */
const CM_PAGES_TTL = 5 * 60 * 1000;
let cmPagesPromise = null;
let cmPagesLoadedAt = 0;

export function fetchCmPages(force = false) {
  const expired = Date.now() - cmPagesLoadedAt > CM_PAGES_TTL;

  if (force || !cmPagesPromise || expired) {
    cmPagesLoadedAt = Date.now();
    cmPagesPromise = get("/pages", {})
      .then((d) => d.pages || [])
      .catch((e) => {
        cmPagesPromise = null; // не кэшируем ошибку
        cmPagesLoadedAt = 0;
        throw e;
      });
  }
  return cmPagesPromise;
}

/**
 * Страницы Lucmus → page_ids для CM.
 *
 * ВАЖНО: в CM пустой page_ids означает «ВСЕ страницы» (empty(...) OR page_id IN ...).
 * Поэтому пустой результат нельзя отправлять в запрос — иначе вместо «ничего нет»
 * пользователь увидел бы статистику по чужим сообществам. Такой скоуп мы
 * помечаем как unmatched, и секции показывают заглушку вместо запроса.
 */
export function resolveScope(pages, { fresh = false } = {}) {
  return fetchCmPages(fresh).then((cmPages) => {
    const known = new Set(cmPages.map((p) => Number(p.page_id)));

    // page_id — это pages.id в БД Lucmus: он уникален по всем соцсетям,
    // в отличие от owner_id (id страницы внутри соцсети, может пересекаться)
    const all = pages || [];
    const pageIds = all.map((p) => Number(p.page_id)).filter((id) => known.has(id));

    // соцсеть и ссылку на страницу CM не отдаёт, но они есть в страницах Lucmus,
    // которые мы и отправляли в запрос: связываем по page_id (= pages.id в БД Lucmus)
    const pageById = new Map(
      all.map((p) => [
        Number(p.page_id),
        { social: String(p.social || "").toUpperCase(), url: p.owner_url || "" },
      ]),
    );

    return {
      pageIds,
      matched: pageIds.length,
      // почему страниц не хватило — это и показываем в заглушке
      totalSelected: all.length,
      notInCm: all.length - pageIds.length,
      cmPages,
      pageById,
      unmatched: pageIds.length === 0,
    };
  });
}

/**
 * Общие параметры любого запроса секции: период, страницы и ВКЛАДКА.
 *
 * format (text | media | all) сервер превращает в фильтр по источнику терма:
 * слова из текстов постов, слова из разбора картинок или всё вместе. Отсюда он
 * попадает во все эндпоинты сразу — переключатель в шапке один на вкладку.
 */
const scopeParams = (params, scope) => ({
  ...period(params),
  page_ids: scope.pageIds.join(","),
  format: params.format || undefined,
});

/**
 * Группы блока «Упоминания» → ключи в ответе CM. На вкладке текста это сущности
 * (аккаунты/домены/ссылки), на вкладке картинок — оси самой картинки
 * (категория/форма подачи/бренд). Какие именно пришли, говорит d.types.
 */
const MENTION_GROUP_KEYS = {
  account: "accounts",
  domain: "domains",
  link: "links",
  category: "categories",
  content_type: "content_types",
  brand: "brands",
};

/* ---------- агрегация ряда (неделя/месяц считаются на клиенте) ---------- */

function bucketOf(date, granularity) {
  if (granularity === "month") return date.slice(0, 7); // YYYY-MM
  if (granularity === "week") {
    const d = new Date(date);
    const day = (d.getUTCDay() + 6) % 7; // понедельник — начало недели
    d.setUTCDate(d.getUTCDate() - day);
    return d.toISOString().slice(0, 10);
  }
  return date;
}

/**
 * Все корзины периода подряд — ось X для рядов. API отдаёт только дни с
 * упоминаниями, и если строить ось из них, пропуски исчезают: слово с одним
 * упоминанием в январе и одним в августе рисовалось ровной линией «1» через
 * весь год. Ключи корзин те же, что у bucketOf, — по ним ряды и раскладываются.
 */
export function bucketsBetween(start, end, granularity) {
  if (!start || !end) return [];
  const out = [];
  const from = bucketOf(start, granularity);
  const to = bucketOf(end, granularity);
  const cursor = new Date(from.length === 7 ? `${from}-01` : from);
  const step = () => {
    if (granularity === "month") cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    else cursor.setUTCDate(cursor.getUTCDate() + (granularity === "week" ? 7 : 1));
  };
  for (let guard = 0; guard < 4000; guard += 1) {
    const key = granularity === "month"
      ? cursor.toISOString().slice(0, 7)
      : cursor.toISOString().slice(0, 10);
    if (key > to) break;
    out.push(key);
    step();
  }
  return out;
}

/**
 * Грануляция по умолчанию — по длине периода: дневной ряд за полгода при паре
 * постов в неделю нечитаем, неделя/месяц показывают динамику, а не гребёнку.
 */
export function defaultGranularity(start, end) {
  if (!start || !end) return "day";
  const days = Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
  if (days <= 45) return "day";
  if (days <= 270) return "week";
  return "month";
}

/** Упоминания складываем, относительные метрики усредняем. */
export function aggregateSeries(series, granularity) {
  if (granularity === "day") return series;

  const buckets = new Map();
  series.forEach((point) => {
    const key = bucketOf(point.date, granularity);
    if (!buckets.has(key)) buckets.set(key, { date: key, mentions: 0, er: [], tr: [] });
    const b = buckets.get(key);
    b.mentions += point.mentions;
    b.er.push([point.rel_er, point.er_n]);
    b.tr.push([point.rel_tr, point.tr_n]);
  });

  // Среднее за неделю/месяц — ПО ПОСТАМ: дневная точка входит с весом «постов с
  // метрикой за день» (er_n/tr_n от сервера), иначе день с одним постом весил бы
  // столько же, сколько день с тридцатью, и цифра расходилась бы с топами.
  // null = метрики нет (напр. у страницы нет комментарийного фона → относит. TR
  // не считается). Такие точки исключаем, а не приравниваем к нулю: иначе неделя
  // и месяц занижали бы среднее. Нет ни одного значения — отдаём null, чтобы
  // «данных нет» дожило до графика (echarts рисует разрыв, а не линию по нулю).
  const avg = (pairs) => {
    let sum = 0;
    let n = 0;
    pairs.forEach(([value, weight]) => {
      if (value == null) return;
      const w = weight == null ? 1 : Number(weight); // старый ответ без весов — по дням
      sum += value * w;
      n += w;
    });
    return n ? Math.round(sum / n) : null;
  };
  return [...buckets.values()].map((b) => ({
    date: b.date,
    mentions: b.mentions,
    rel_er: avg(b.er),
    rel_tr: avg(b.tr),
  }));
}

/**
 * Вложения поста → формат коллажа обычной ленты ({ type, image_url, video_url }).
 * CM в формате «Текст» медиа не присылает, поэтому это задел на будущее: как
 * только в items появятся attachments/photos/media — коллаж заработает без правок.
 */
function mediaOf(p) {
  const raw = p.attachments || p.photos || p.media || [];
  return raw
    .map((a, index) => ({
      // index — позиция во ВЛОЖЕНИЯХ поста, как их нумерует сервер: по нему
      // сходятся значки «разобрано» и сам разбор (media_index)
      index,
      type: a.type === "video" ? "video" : "photo",
      image_url: a.image_url || a.url || a.src || "",
      video_url: a.video_url || "",
    }))
    .filter((a) => a.image_url || a.video_url);
}

/**
 * VK-нативные видео → массив URL встроенного плеера (video_ext.php). Прямого
 * файла у них нет, поэтому показываем их iframe'ом (как обычная лента Lucmus),
 * отдельно от коллажа. VK API не нужен — плеер строит ingest из owner_id/video_id.
 */
function embedsOf(p) {
  const raw = p.media || [];
  return raw
    .map((a, index) => ({ index, url: (a && a.embed_url) || "", type: a && a.type }))
    .filter((a) => a.type === "embed" && a.url);
}

/**
 * IG Reel/Post → URL встроенного плеера Instagram (/embed).
 * Нужен как запасной вариант, когда прямой video_url с CDN протух или битый.
 */
function igEmbedUrl(postUrl) {
  if (!postUrl) return "";
  const match = String(postUrl).match(/instagram\.com\/(reel|p|tv)\/([^/?#]+)/i);
  if (!match) return "";
  return `https://www.instagram.com/${match[1].toLowerCase()}/${match[2]}/embed`;
}

/**
 * Страницы воркспейса, которые попадают под авто-сбор текстов, но ещё НЕ заведены
 * в CM (сбор в процессе). По ним показываем прелоадер «собираем данные…» вместо
 * заглушки «страница не в мониторинге». Некритично: при ошибке — пустой список.
 */
/**
 * Запрос к модели, которым описывается картинка.
 *
 * Стандартный текст живёт на сервере (там же он и применяется по умолчанию),
 * правленый — в localStorage браузера: это инструмент подбора формулировок, и
 * держать его на сервере рано. Пока правленый совпадает со стандартным, на
 * сервер его не шлём — тогда работает кэш разборов и ответ сохраняется.
 */
const IMAGE_PROMPT_KEY = "cm-image-prompt";
let imagePromptPromise = null;
let imagePromptLang = "";
let defaultImagePrompt = "";

/**
 * Язык ответа модели — тот, что выбран в приложении (i18nSetup хранит его в
 * localStorage). Сервер знает только свой язык по умолчанию, поэтому шлём явно.
 */
export function appLang() {
  try {
    return localStorage.getItem("userLanguage") || "";
  } catch (e) {
    return "";
  }
}

export function fetchImagePrompt() {
  const lang = appLang();
  if (imagePromptPromise && imagePromptLang !== lang) {
    imagePromptPromise = null; // язык сменили — стандартный текст другой
  }
  imagePromptLang = lang;
  if (!imagePromptPromise) {
    imagePromptPromise = get("/image-prompt", { lang: lang || undefined })
      .then((d) => {
        defaultImagePrompt = d.prompt || "";
        return d;
      })
      .catch((e) => {
        imagePromptPromise = null; // не кэшируем ошибку
        throw e;
      });
  }
  return imagePromptPromise;
}

export function readImagePrompt() {
  try {
    return localStorage.getItem(IMAGE_PROMPT_KEY) || "";
  } catch (e) {
    return ""; // приватный режим/запрет хранилища — просто работаем без сохранения
  }
}

export function saveImagePrompt(text) {
  try {
    if (text) localStorage.setItem(IMAGE_PROMPT_KEY, text);
    else localStorage.removeItem(IMAGE_PROMPT_KEY);
  } catch (e) {
    /* хранилище недоступно — правка живёт до перезагрузки страницы */
  }
}

/** Что отправлять серверу: undefined, если промпт не отличается от стандартного. */
export function customImagePrompt() {
  const saved = readImagePrompt().trim();
  if (!saved) return undefined;
  if (defaultImagePrompt && saved === defaultImagePrompt.trim()) return undefined;
  return saved;
}

/** Сохранённые разборы картинок поста (модалка результата, модель не дёргается). */
export function fetchImageDescriptions(pageId, postId) {
  return get("/image-descriptions", { page_id: pageId, post_id: postId })
    .then((d) => d.items || []);
}

export function fetchPendingPages() {
  return get("/pending-pages", {})
    .then((d) => (d.page_ids || []).map(Number))
    .catch(() => []);
}

/* ---------- эндпоинты ---------- */

export default {
  resolveScope,
  fetchCmPages,
  fetchPendingPages,

  /* ---------- отчёты (MVP-8): PPTX / PDF / XLSX по данным вкладки ---------- */

  /**
   * Собрать отчёт на сервере: та же вкладка (format), период и страницы, что
   * на экране; lang — язык интерфейса, на нём подписи. Сервер отвечает карточкой
   * ({hash, type, …}), файл скачивается отдельным запросом (downloadReport).
   */
  createReport({ type, format, period, pageIds, lang }) {
    return post("/reports", {
      type,
      format: format || "text",
      date_from: (period && period.start) || undefined,
      date_to: (period && period.end) || undefined,
      page_ids: (pageIds || []).join(",") || undefined,
      lang,
    });
  },

  fetchReports() {
    return get("/reports").then((d) => d.reports || []);
  },

  /** Файл отчёта как Blob (+ имя из Content-Disposition), чтобы отдать браузеру на скачивание. */
  downloadReport(hash) {
    return http
      .get(`${PREFIX}/reports/${hash}`, { responseType: "blob", timeout: 120000 })
      .then((r) => {
        const cd = r.headers["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        return { blob: r.data, filename: m ? m[1] : `report-${hash.slice(0, 8)}` };
      })
      .catch((e) => {
        throw new Error(toMessage(e));
      });
  },

  deleteReport(hash) {
    return http.delete(`${PREFIX}/reports/${hash}`).then((r) => r.data).catch((e) => {
      throw new Error(toMessage(e));
    });
  },

  fetchOverview(params, scope) {
    return get("/overview", scopeParams(params, scope)).then((d) => {
      // summary может не прийти (ошибка на стороне CM отдаёт пустое тело) —
      // без подстраховки секция падала бы с TypeError вместо понятного «нет данных»
      const summary = d.summary || {};

      return {
        totals: {
          posts_count: summary.total_posts || 0,
          unique_terms: summary.unique_terms || 0,
          pages_with_posts: summary.pages_with_data || 0,
          pages_total: summary.total_monitored_pages || 0,
        },
        tops: {
          by_posts: d.top_used_terms || [],
          by_er: d.top_activity_terms || [],
          by_tr: d.top_discussion_terms || [],
        },
        // обсуждаемость есть не у всех соцсетей — топ по TR тогда прячем
        discussion_available: d.discussion_available,
        tracked_terms: d.tracked_terms || [],
        // популярные словосочетания (n-граммы): label — читаемая форма для показа,
        // term — нормализованная фраза (ключ клика: лента/drill-down)
        phrases: (d.top_phrases || []).map((p) => ({
          term: p.term,
          label: p.phrase,
          posts_count: p.posts_count,
          avg_relative_er: p.avg_relative_er,
          avg_relative_tr: p.avg_relative_tr,
        })),
      };
    });
  },

  /** Отдаёт дневной ряд; неделю/месяц компонент считает через aggregateSeries. */
  fetchTimelines(params, scope) {
    return get("/timelines", scopeParams(params, scope)).then((d) => ({
      terms: (d.terms || []).map((t) => ({
        term: t.term,
        added: !!t.added,
        series: (t.series || []).map((p) => ({
          date: p.date,
          mentions: p.mentions,
          rel_er: p.rel_activity,
          rel_tr: p.rel_discussion,
          // постов с валидной метрикой за день — вес точки в aggregateSeries
          er_n: p.er_posts,
          tr_n: p.tr_posts,
        })),
      })),
    }));
  },

  fetchTrends(params, scope) {
    const added = new Set(params.added || []);

    return get("/trends", scopeParams(params, scope)).then((d) => ({
      low_data: !!d.low_data,
      // окно, порог и объём свежего окна — чтобы объяснить пустой блок, а не прятать его
      recent_days: (d.window && d.window.recent_window_days) || 0,
      min_recent_posts: (d.thresholds && d.thresholds.min_recent_posts) || 0,
      recent_posts_total: d.recent_posts_total || 0,
      rising: (d.trends || []).map((t) => ({
        term: t.term,
        added: added.has(t.term),
        recent: t.recent_posts_count,
        baseline: t.baseline_posts_count,
        // trend_ratio: во сколько раз выросло → в проценты прироста
        growth_pct: Math.round((t.trend_ratio - 1) * 100),
      })),
      new: (d.new_signals || []).map((t) => ({
        term: t.term,
        added: added.has(t.term),
        posts_count: t.recent_posts_count,
      })),
    }));
  },

  fetchMentions(params, scope) {
    const group = (g) => {
      const total = (g && g.total_mentions) || 0;
      return {
        total,
        items: ((g && g.items) || []).map((i) => ({
          term: i.term || i.entity,
          // ключ как он лежит в базе: у категорий и форм подачи это латинский
          // слаг, который на экране заменяется переводом (см. image-labels.js)
          entity: i.entity,
          mentions: i.mentions,
          share_pct: total ? Math.round((i.mentions / total) * 1000) / 10 : 0,
          // null = упоминаний слишком мало, чтобы называть это средним (порог на сервере);
          // у отслеживаемых (★) сущностей значение приходит всегда
          avg_relative_er: i.avg_activity,
          tracked: !!i.tracked,
        })),
      };
    };

    return get("/mentions", scopeParams(params, scope)).then((d) => {
      const types = d.types && d.types.length ? d.types : ["account", "link", "brand"];
      // порог сервера — чтобы подписать, почему у строки нет средней активности
      const out = { min_posts_for_avg: d.min_posts_for_avg || 0, types };
      types.forEach((type) => {
        const key = MENTION_GROUP_KEYS[type];
        if (key) out[key] = group(d[key]);
      });
      return out;
    });
  },

  fetchFeed(params, scope) {
    const perPage = params.perPage || 10;
    const page = params.page || 1;

    return get("/feed", {
      ...scopeParams(params, scope),
      // фильтр по терму и текстовый поиск — это один и тот же параметр q;
      // term может быть НАБОРОМ слов (из drill-down) — сервер ждёт их через запятую
      // и отбирает посты, где встречаются все сразу
      q:
        (Array.isArray(params.term) ? params.term.join(",") : params.term) ||
        params.query ||
        undefined,
      // откуда слово фильтра: чип с картинки ищем по картинкам даже на вкладке
      // «текст» (в тексте его нет — лента была бы пустой); без него — как вкладка
      term_source: params.termSource || undefined,
      // терм из «Упоминаний» (аккаунт/ссылка) — искать как упоминание, не как слово
      entity: params.entity || undefined,
      sort: params.sort || "activity",
      order: params.order || "desc",
      limit: perPage,
      offset: (page - 1) * perPage,
    }).then((d) => ({
      total: d.total || 0,
      // разбор картинок — платная функция: на тестовом доступе сервер его не даёт
      // (и массово такие страницы не разбирает), поэтому значок «разобрать» не рисуем.
      // Флаг ответа означает «доступен хоть где-то»: в ленте бывают и свои страницы,
      // и расшаренные, а платит владелец каждой — точное право приходит у поста
      imageAnalysis: d.image_analysis !== false,
      items: (d.items || []).map((p) => ({
        id: `${p.page_id}_${p.post_id}`,
        // page_id (= pages.id в БД Lucmus) нужен, чтобы подтянуть аватар страницы:
        // CM аватарки не отдаёт, берём их из страниц Lucmus (см. Cm-feed)
        page_id: p.page_id,
        // id поста в соцсети — нужен разбору картинок (см. describeImage)
        post_id: p.post_id,
        page_name: p.page_title,
        // право на разбор картинок ЭТОГО поста: у расшаренной страницы его даёт
        // тариф владельца. Старый сервер поля не присылает — тогда флаг ответа
        imageAnalysis:
          p.image_analysis === undefined
            ? d.image_analysis !== false
            : p.image_analysis !== false,
        // короткое имя (@screen_name) CM отдаёт — показываем под названием
        screen_name: p.screen_name || "",
        // соцсеть приходит из CM: данные мультисетевые (vk/fb/ig/tg)
        social: String(p.social || "").toUpperCase(),
        date: p.post_date,
        text: p.text_snippet,
        terms: p.terms || [],
        // теги с картинок: {key, label} — key (лемма) для поиска/drill-down,
        // label — тег как его дала модель. Бренды и известные люди сервер
        // отдаёт все, без лимита
        mediaTerms: p.media_terms || [],
        brands: p.brands || [],
        // бренды и известные люди из ТЕКСТА поста (разметка моделью): чип ведёт
        // в drill-down по тексту, тогда как brands — по картинкам
        textBrands: p.text_brands || [],
        // медиа поста: фото → коллаж; прямые видео → <video>; VK → embeds;
        // IG Reel embed — запасной вариант, если video_url битый/протух
        images: mediaOf(p),
        embeds: embedsOf(p),
        // индексы вложений, которые уже разобраны моделью (значок-галочка)
        analyzed: p.analyzed || [],
        reelEmbed: igEmbedUrl(p.post_url),
        likes: p.likes,
        comments: p.comments,
        reposts: p.shares,
        views: p.views,
        er: p.er_post,
        relative_er: p.relative_er,
        tr: p.tr_post,
        relative_tr: p.relative_tr,
        link: p.post_url,
      })),
    }));
  },

  /**
   * Описание одной картинки поста моделью (MVP-7, пока кнопкой в ленте).
   *
   * Ссылку сервер принимает только «свою»: она обязана совпасть с вложением
   * этого поста, иначе 400. Повторный клик отвечает из кэша (cached=true),
   * force пересчитывает, tier=2 сразу берёт дорогую модель.
   *
   * Таймаут отдельный: скачать картинку + сходить в модель дольше обычных 30 с.
   */
  describeImage({ pageId, postId, imageUrl, force = false, tier = 0, prompt }) {
    return post(
      "/image-description",
      { page_id: pageId, post_id: postId, image_url: imageUrl, force, tier, prompt,
        lang: appLang() || undefined },
      { timeout: 120000 },
    );
  },

  fetchTerm(params, scope) {
    // соцсеть и ссылку CM не отдаёт — берём их из страниц Lucmus по page_id (см. resolveScope)
    const pageOf = (id) => (scope.pageById && scope.pageById.get(Number(id))) || {};

    // extra — доп. слова набора (свободный ввод): сервер их лемматизирует и возвращает
    // канонический набор в d.terms, отбирая посты, где встречаются ВСЕ слова сразу
    const extra = (params.extra || []).join(",") || undefined;

    return get("/term", {
      ...scopeParams(params, scope),
      term: params.term,
      extra,
      entity: params.entity || undefined,
      tz: BROWSER_TZ,
    }).then((d) => {
      const weight = (d.author_weight && d.author_weight.by_activity) || [];
      const rhythm = d.rhythm || {};
      return {
        term: d.term,
        // канонический набор (кликнутый терм + лемматизированный свободный ввод)
        terms: d.terms || [d.term],
        // постов, где есть ВСЕ слова набора — размер выборки под всеми метриками
        postsCount: d.posts_count || 0,
        // средние за период по постам набора — той же формулой, что колонки топов
        // (undefined у старого API → компонент усреднит дневной ряд сам)
        avgRelativeEr: d.avg_relative_er,
        avgRelativeTr: d.avg_relative_tr,
        weight: weight.map((w) => {
          const page = pageOf(w.page_id);
          return {
            page_name: w.page_title,
            social: page.social || "",
            link: page.url || "",
            posts_count: w.posts_with_term,
            contribution_pct: w.contrib_pct,
          };
        }),
        share: (d.word_share || []).map((s) => {
          const page = pageOf(s.page_id);
          return {
            page_name: s.page_title,
            social: page.social || "",
            link: page.url || "",
            mention_share_pct: s.share_posts_pct,
            content_share_pct: s.intensity_pct,
          };
        }),
        // дневной ряд упоминаний терма (как в fetchTimelines): неделю/месяц компонент
        // считает через aggregateSeries; разрежённые дни достраивает ось графика
        series: (d.series || []).map((p) => ({
          date: p.date,
          mentions: p.mentions,
          rel_er: p.rel_activity,
          rel_tr: p.rel_discussion,
          er_n: p.er_posts,
          tr_n: p.tr_posts,
        })),
        // ритм слова: сервер отдаёт ПЛОТНЫЕ массивы (7 дней и 24 часа), достраивать нечего
        tz: rhythm.tz || "",
        byWeekday: rhythm.by_weekday || [],
        byHour: rhythm.by_hour || [],
      };
    });
  },

  /* ---------- списки слов (живут на сервере, в воркспейсе CM) ---------- */

  fetchWordLists() {
    return get("/word-lists", {}).then((d) => ({
      added: d.added || [],
      excluded: d.excluded || [],
    }));
  },

  /**
   * normalize=true — слово вписано руками, сервер приводит его к начальной форме
   * (та же лемматизация, что при разборе постов). Клик по готовому терму шлёт
   * normalize=false: он уже нормализован, а хэштеги не лемматизируются.
   */
  addWord(listType, word, { normalize = false } = {}) {
    return http
      .post(`${PREFIX}/word-lists`, { list_type: listType, word, normalize })
      .then((r) => r.data)
      .catch((e) => {
        throw new Error(toMessage(e));
      });
  },

  removeWord(listType, word) {
    return http
      .delete(`${PREFIX}/word-lists`, { params: { list_type: listType, word } })
      .then((r) => r.data)
      .catch((e) => {
        throw new Error(toMessage(e));
      });
  },
};
